import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slider,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  AddPhotoAlternateOutlined as AddPhotoAlternateOutlinedIcon,
} from '@mui/icons-material';
import LinearProgressBar from 'src/components/progress-bar/progressBar.component';
import { CommonHelper } from 'src/utils/commonHelper';
import { PostUtil } from '../post-util';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useTranslation } from 'react-i18next';
import TextIconLabel from 'src/components/TextIconLabel';

const CommonThumbnail = ({ videoSrc, handleClose, handleChangeThumbnail, serviceName, profile }) => {
  const { t: translate } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [error, setError] = useState([]);
  const [fileUploadprogress, setFileUploadprogress] = useState();
  const { showToast } = useToaster();
  const [blobUrl, setBlobUrl] = useState(null);
  const [frameCaptured, setFrameCaptured] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const theme = useTheme();

  // Platform-specific configurations
  const configurations = {
    youtube: {
      MAX_IMAGE_SIZE_MB: 2,
      hasCustomThumbnailPermission: profile?.connected_profile_details?.has_custom_thumbnail_permission || false,
    },
    facebook: PostUtil.ServiceConfigurations.FACEBOOK.POST,
    instagram: PostUtil.ServiceConfigurations.INSTAGRAM.REELS,
    pinterest: PostUtil.ServiceConfigurations.PINTEREST.POST,
  };

  const config = configurations[serviceName.toLowerCase()] || {
    MAX_IMAGE_SIZE_MB: 2,
    hasCustomThumbnailPermission: true,
  };

  useEffect(() => {
    let isMounted = true;

    if (videoSrc && videoSrc.media_url) {
      const convertVideoToBlobUrl = async () => {
        try {
          const response = await fetch(videoSrc.media_url);
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          if (isMounted) {
            setBlobUrl(url);
          }
        } catch (error) {
          console.error(translate('post_conditions.error_converting_video_src_to_blob_url'), error);
        }
      };

      convertVideoToBlobUrl();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [videoSrc]);

  const failureCallback = (message) => {
    showToast(message, 'e');
  };

  const successCallback = (data, message) => {
    let iErrors = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i]?.file_size !== 0 && data[i]?.file_size > config.MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        iErrors.push(
          `${translate('post_conditions.image_size_must_less')} ${config.MAX_IMAGE_SIZE_MB} MB. ${translate(
            'post_conditions.your_image_is'
          )} ${(data[i]?.file_size / 1024 / 1024).toFixed(2)} MB.`
        );
        message &&
          showToast(
            `${translate('post_conditions.image_size_must_less')} ${config.MAX_IMAGE_SIZE_MB} MB. ${translate(
              'post_conditions.your_image_is'
            )} ${(data[i]?.file_size / 1024 / 1024).toFixed(2)} MB.`,
            'e'
          );
      }
    }

    setError(iErrors);
    if (iErrors.length === 0) {
      message && showToast(message, 's');
      handleChangeThumbnail(data[0]?.media_url);
      handleClose();
    }
  };

  const captureFirstFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'thumbnail.jpeg', { type: 'image/jpeg' });
          setThumbnails([file]);
          setSelectedThumbnail(file);
          setError([]);
        } else {
          setError([translate('post_conditions.failed_to_capture_frame')]);
        }
      }, 'image/jpeg');
      setFrameCaptured(true);
    } catch (error) {
      setError(['Failed to capture frame. This may be due to cross-origin content.']);
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `thumbnail_${Date.now()}.jpeg`, { type: 'image/jpeg' });
          setThumbnails((prev) => [...prev, file]);
          setSelectedThumbnail(file);
          setError([]);
        } else {
          setError([translate('post_conditions.failed_to_capture_image')]);
        }
      }, 'image/jpeg');
    } catch (error) {
      setError([translate('post_conditions.failed_to_capture_frame_cross_origin')]);
    }
  };
  const handleVideoLoaded = () => {
    const video = videoRef.current;

    if (!frameCaptured) {
      video.currentTime = 0;

      video.onseeked = () => {
        if (!frameCaptured) {
          captureFirstFrame();
          video.onseeked = null;
        }
      };
    }
    setDuration(video.duration);
  };

  const handleSliderChange = (event, newValue) => {
    setCurrentTime(newValue);
    videoRef.current.currentTime = newValue;
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleThumbnailSelect = (thumbnail) => {
    setSelectedThumbnail(thumbnail);
  };

  const handleThumbnailUpload = () => {
    let uploadFilesProps = {
      files: [selectedThumbnail],
      setFileUploadprogress,
      successCallback,
      failureCallback,
    };
    CommonHelper.UploadMediaFilesToServer(uploadFilesProps);
  };

  const handleFileChange = (event) => {
    const maxFileSizeInBytes = 6 * 1073741824; // 6 GB
    const files = Array.from(event.target.files);
    let supportedFiles = files.filter((a) => PostUtil.SupportedFileFormat.includes(a.type));

    if (supportedFiles.length > 0) {
      if (!supportedFiles.some((a) => a.size > maxFileSizeInBytes)) {
        setThumbnails((prev) => [...prev, ...supportedFiles]);
        setSelectedThumbnail(supportedFiles[0]);
      } else {
        showToast(
          `${translate('media_library.max_file_size')} 6GB. ${translate('media_library.select_below_max_size')} 6 GB`,
          'e'
        );
      }
    } else {
      let errorMessage = `${translate('common.we_only_support')} JPEG, JPG, PNG & MP4 ${translate(
        'common.file_formats'
      )}`;
      showToast(errorMessage, 'e');
    }
  };

  const onClose = () => {
    handleClose();
  };

  const errorView = () => {
    return (
      <Box style={{ marginTop: '0.5rem' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{translate('post_conditions.error_description')}</Typography>
        {error?.map((errorMessage) => (
          <TextIconLabel
            key={errorMessage}
            icon={<ErrorOutlineIcon sx={{ color: theme.palette.error.main, mr: 1, fontSize: 18 }} />}
            value={errorMessage}
            sx={{ typography: 'caption', color: theme.palette.error.main, mb: 1, fontWeight: 800, fontSize: 14 }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      PaperProps={{
        style: {
          width: '80%',
          maxWidth: '800px',
        },
      }}
    >
      <DialogTitle>
        {translate(
          serviceName.toLowerCase() === 'youtube'
            ? 'post_conditions.upload_thumbnail'
            : 'post_conditions.upload_cover_image'
        )}
      </DialogTitle>
      <DialogContent>
        {blobUrl ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <video
                ref={videoRef}
                src={blobUrl}
                controls
                crossOrigin="anonymous"
                onLoadedData={handleVideoLoaded}
                onTimeUpdate={handleTimeUpdate}
                style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              <Box my={1}>
                <Typography variant="h6">
                  {translate('post_conditions.slide_to_select_cover', 'Slide to select your Cover Image')}
                </Typography>
                <Slider
                  value={currentTime}
                  onChange={handleSliderChange}
                  min={0}
                  max={duration}
                  aria-labelledby="video-slider"
                  valueLabelDisplay="auto"
                  sx={{ color: 'primary.main' }}
                />
              </Box>

              <Box my={1} display="flex" justifyContent="center">
                <Button variant="contained" color="primary" onClick={captureFrame}>
                  {translate('post_conditions.capture_frame', 'Capture Frame')}
                </Button>
                {((serviceName.toUpperCase() === PostUtil.ServiceName.YOUTUBE && config.hasCustomThumbnailPermission) || serviceName !== PostUtil.ServiceName.YOUTUBE) && (
                  <Button variant="contained" component="label" sx={{ ml: 1 }}>
                    {translate('post_conditions.upload_from_device', 'Upload from Device')}
                    <input type="file" accept="image/*" onChange={handleFileChange} hidden />
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={12}>
              {error.length > 0 && (
                <Box>
                  <Typography variant="body1" color="error">
                    {errorView()}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              {thumbnails.length > 0 && (
                <Box>
                  <Typography variant="h6">
                    {translate('post_conditions.select_your_cover_image', 'Select Your Cover Image')}
                  </Typography>
                  <Grid container spacing={1}>
                    {thumbnails.map((file, index) => (
                      <Grid item xs={4} sm={3} md={2} key={index}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Thumbnail ${index}`}
                          style={{
                            border: selectedThumbnail === file ? `3px solid ${theme.palette.primary.main}` : 'none',
                            cursor: 'pointer',
                            width: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            boxSizing: 'border-box',
                          }}
                          onClick={() => handleThumbnailSelect(file)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {selectedThumbnail && (
                <Box my={2}>
                  <Typography variant="h6">
                    {translate('post_conditions.cover_image_preview', 'Cover Image Preview')}
                  </Typography>
                  <img
                    src={URL.createObjectURL(selectedThumbnail)}
                    alt="Selected Thumbnail"
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        ) : (
          <Spinner />
        )}
      </DialogContent>
      <DialogActions
        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div style={{ flex: 1 }}>{fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}</div>
        <Button variant="outlined" color="error" onClick={onClose}>
          {translate('common.cancel', 'Cancel')}
        </Button>
        {selectedThumbnail && (
          <Button variant="contained" onClick={handleThumbnailUpload}>
            {translate('common.upload', 'Upload')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CommonThumbnail;
